import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * SatelliteMapPanel — Right-side panel showing satellite view with history selector.
 *
 * Renders a farm field visualization colored by NDVI intensity,
 * plus a year-based timeline to browse historical snapshots.
 *
 * @param {object[]}  ndviSnapshots  – Array of { year, ndvi, healthPct, note }
 * @param {object}    farm           – Farm object with coords, satelliteTimeseriesRequest, name, fields
 * @param {boolean}   isArabic       – RTL mode
 */
const SatelliteMapPanel = ({ ndviSnapshots = [], farm = {}, isArabic }) => {
  const years = useMemo(() => ndviSnapshots.map((s) => s.year), [ndviSnapshots]);
  const [selectedYear, setSelectedYear] = useState(
    years.length > 0 ? years[years.length - 1] : null
  );

  const snapshot = useMemo(
    () => ndviSnapshots.find((s) => s.year === selectedYear) || null,
    [ndviSnapshots, selectedYear]
  );

  const ndviValue = snapshot ? parseFloat(snapshot.ndvi) : 0;
  const healthPct = snapshot?.healthPct ? parseInt(snapshot.healthPct, 10) : 0;

  // NDVI → color mapping (desert brown → lush green)
  const ndviColor = useMemo(() => {
    if (ndviValue <= 0.1) return "#c2a059";     // bare desert
    if (ndviValue <= 0.2) return "#a8b455";     // sparse
    if (ndviValue <= 0.35) return "#7fb55a";    // early growth
    if (ndviValue <= 0.5) return "#4ea651";     // moderate
    if (ndviValue <= 0.65) return "#2d944a";    // healthy
    return "#1a7a3a";                           // very healthy
  }, [ndviValue]);

  const ndviBg = useMemo(() => {
    if (ndviValue <= 0.1) return "rgba(194,160,89,0.18)";
    if (ndviValue <= 0.35) return "rgba(127,181,90,0.18)";
    return "rgba(26,122,58,0.18)";
  }, [ndviValue]);

  // Build field polygon coordinates from satelliteTimeseriesRequest
  const area = farm.satelliteTimeseriesRequest?.area || [];

  // Generate field plot shapes (stylized SVG representation)
  const fields = farm.preview?.fields || [];

  return (
    <div className="h-full flex flex-col bg-[#0a1a12] rounded-2xl border border-emerald-900/40 overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 border-b border-emerald-900/30 flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
        <div className={`flex items-center gap-2 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">
            {isArabic ? "عرض القمر الصناعي" : "Satellite View"}
          </span>
        </div>
        {farm.coords && (
          <span className="text-[10px] font-mono text-slate-500">
            {farm.coords}
          </span>
        )}
      </div>

      {/* Main satellite visualization */}
      <div className="flex-1 relative min-h-[280px]">
        {/* Background grid pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="satGrid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#10b981" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#satGrid)" />
        </svg>

        {/* Radial glow based on NDVI intensity */}
        <div
          className="absolute inset-0 transition-all duration-700"
          style={{
            background: `radial-gradient(ellipse at center, ${ndviBg} 0%, transparent 70%)`,
          }}
        />

        {/* Field plots SVG */}
        <svg
          viewBox="0 0 300 250"
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Stylized field outlines — positions derived from field count */}
          {fields.length > 0 ? (
            fields.map((field, i) => {
              const cols = Math.ceil(Math.sqrt(fields.length));
              const row = Math.floor(i / cols);
              const col = i % cols;
              const cellW = 240 / cols;
              const cellH = 200 / Math.ceil(fields.length / cols);
              const x = 30 + col * cellW + 4;
              const y = 25 + row * cellH + 4;
              const w = cellW - 8;
              const h = cellH - 8;

              // Per-field health blended with selected snapshot
              const fieldHealth = field.cropHealth || 50;
              const blendedNdvi = ndviValue * 0.6 + (fieldHealth / 100) * 0.4;
              const fieldColor =
                blendedNdvi <= 0.2 ? "#8b7a42" :
                blendedNdvi <= 0.4 ? "#6d9448" :
                blendedNdvi <= 0.6 ? "#4ea651" :
                "#2d944a";
              const fieldOpacity = 0.35 + blendedNdvi * 0.45;

              return (
                <g key={field.id}>
                  <motion.rect
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rx={6}
                    fill={fieldColor}
                    fillOpacity={fieldOpacity}
                    stroke={fieldColor}
                    strokeWidth={1.2}
                    strokeOpacity={0.6}
                    initial={{ fillOpacity: 0 }}
                    animate={{ fillOpacity: fieldOpacity }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                  />
                  {/* Field label */}
                  <text
                    x={x + w / 2}
                    y={y + h / 2 - 4}
                    textAnchor="middle"
                    className="fill-white/80 text-[8px] font-mono"
                  >
                    {field.title?.split("–")[0]?.trim() || `F${i + 1}`}
                  </text>
                  <text
                    x={x + w / 2}
                    y={y + h / 2 + 8}
                    textAnchor="middle"
                    className="fill-emerald-300/60 text-[7px] font-mono"
                  >
                    {field.area}
                  </text>
                </g>
              );
            })
          ) : (
            /* Fallback: single area polygon */
            <motion.rect
              x={40}
              y={40}
              width={220}
              height={170}
              rx={12}
              fill={ndviColor}
              fillOpacity={0.3}
              stroke={ndviColor}
              strokeWidth={1.5}
              strokeOpacity={0.5}
              initial={{ fillOpacity: 0 }}
              animate={{ fillOpacity: 0.3 }}
              transition={{ duration: 0.8 }}
            />
          )}
        </svg>

        {/* NDVI overlay badge */}
        <AnimatePresence mode="wait">
          {snapshot && (
            <motion.div
              key={selectedYear}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className={`absolute top-4 ${isArabic ? "left-4" : "right-4"} bg-black/60 backdrop-blur-md rounded-xl border border-emerald-500/30 px-4 py-3 min-w-[130px]`}
            >
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-400 mb-1.5">
                {isArabic ? "مؤشر NDVI" : "NDVI Index"}
              </div>
              <div className="text-3xl font-bold text-white tabular-nums leading-none">
                {ndviValue.toFixed(2)}
              </div>
              {healthPct > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: ndviColor }}
                      initial={{ width: 0 }}
                      animate={{ width: `${healthPct}%` }}
                      transition={{ duration: 0.6, delay: 0.15 }}
                    />
                  </div>
                  <span className="text-xs font-bold text-emerald-300 tabular-nums">
                    {healthPct}%
                  </span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Year note at bottom of map */}
        <AnimatePresence mode="wait">
          {snapshot?.note && (
            <motion.div
              key={`note-${selectedYear}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              className={`absolute bottom-3 left-4 right-4 text-center`}
            >
              <span className="inline-block px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg text-xs text-slate-300 font-medium border border-white/5">
                {snapshot.note}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* NDVI color legend */}
      <div className="px-4 py-2 border-t border-emerald-900/20">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] text-slate-500 font-mono shrink-0">
            {isArabic ? "جاف" : "Bare"}
          </span>
          <div className="flex-1 h-1.5 rounded-full overflow-hidden flex">
            {["#c2a059","#a8b455","#7fb55a","#4ea651","#2d944a","#1a7a3a"].map((c, i) => (
              <div key={i} className="flex-1 h-full" style={{ backgroundColor: c }} />
            ))}
          </div>
          <span className="text-[9px] text-slate-500 font-mono shrink-0">
            {isArabic ? "أخضر" : "Lush"}
          </span>
        </div>
      </div>

      {/* Year timeline selector */}
      <div className="px-4 py-3 border-t border-emerald-900/30 bg-[#061210]">
        <div className={`flex items-center gap-1 ${isArabic ? "flex-row-reverse" : ""}`}>
          <span className="text-[10px] text-slate-500 font-mono mr-2 shrink-0">
            {isArabic ? "السنة" : "Year"}
          </span>
          <div className={`flex items-center gap-1 flex-1 overflow-x-auto no-scrollbar ${isArabic ? "flex-row-reverse" : ""}`}>
            {years.map((year, i) => {
              const isActive = year === selectedYear;
              return (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`relative px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 shrink-0 ${
                    isActive
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      : "bg-white/5 text-slate-400 border border-transparent hover:bg-white/10 hover:text-slate-300"
                  }`}
                >
                  {year}
                  {isActive && (
                    <motion.div
                      layoutId="yearIndicator"
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-400"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mini sparkline of NDVI progression */}
        {ndviSnapshots.length > 1 && (
          <div className="mt-2.5">
            <svg viewBox={`0 0 ${Math.max(ndviSnapshots.length * 40, 100)} 28`} className="w-full h-6">
              {/* Connecting line */}
              <polyline
                points={ndviSnapshots
                  .map((s, i) => {
                    const x = 10 + (i / (ndviSnapshots.length - 1)) * (ndviSnapshots.length * 40 - 20);
                    const y = 24 - parseFloat(s.ndvi) * 24;
                    return `${x},${y}`;
                  })
                  .join(" ")}
                fill="none"
                stroke="#10b981"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
              />
              {/* Data points */}
              {ndviSnapshots.map((s, i) => {
                const totalW = ndviSnapshots.length * 40 - 20;
                const x = 10 + (i / Math.max(ndviSnapshots.length - 1, 1)) * totalW;
                const y = 24 - parseFloat(s.ndvi) * 24;
                const isActive = s.year === selectedYear;
                return (
                  <g key={s.year}>
                    <circle
                      cx={x}
                      cy={y}
                      r={isActive ? 4 : 2.5}
                      fill={isActive ? "#10b981" : "#334155"}
                      stroke={isActive ? "#34d399" : "none"}
                      strokeWidth={isActive ? 1.5 : 0}
                      className="cursor-pointer transition-all"
                      onClick={() => setSelectedYear(s.year)}
                    />
                    {isActive && (
                      <circle cx={x} cy={y} r={7} fill="none" stroke="#10b981" strokeWidth="0.5" opacity="0.4" />
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default SatelliteMapPanel;
